/**
 * What the status page asks the panel: all public, all on this host (docs/STATUS_PAGE.md).
 */

import { useCallback, useEffect, useState } from 'react'

export type Status = 'operational' | 'maintenance' | 'degraded' | 'partial' | 'major'
export type DayStatus = Status | 'none'
export type Kind = 'incident' | 'maintenance' | 'notice'
export type State =
  | 'investigating'
  | 'identified'
  | 'monitoring'
  | 'resolved'
  | 'scheduled'
  | 'in_progress'
  | 'verifying'
  | 'completed'
  | 'active'
  | 'ended'
export type Impact = 'none' | 'minor' | 'major' | 'critical' | 'maintenance'

export interface EventRef {
  id: number
  kind: Kind
  title: string
  impact: Impact
}

export interface Day {
  day: string
  status: DayStatus
  uptime: number | null
  degraded: number
  partial: number
  major: number
  maintenance: number
  events: EventRef[]
}

export interface Component {
  id: number
  name: string
  description: string
  status: Status
  uptime: number | null
  days: Day[]
}

export interface Group {
  name: string
  status: Status
  components: Component[]
}

export interface Update {
  id: number
  state: State
  body: string
  at: string
}

export interface StatusEvent {
  id: number
  kind: Kind
  title: string
  state: State
  impact: Impact
  starts_at: string
  ends_at: string | null
  closed_at: string | null
  created_at: string
  components: { id: number; name: string; status: Status }[]
  updates: Update[]
}

export interface Page {
  name: string
  description: string
  overall: Status
  groups: Group[]
  active: StatusEvent[]
  upcoming: StatusEvent[]
  notices: StatusEvent[]
  recent: StatusEvent[]
  history_days: number
  uptime_days: number
  at: number
}

export interface History {
  items: StatusEvent[]
  page: number
  pages: number
}

/** The city's pulse, from the community site's own endpoint. */
export interface City {
  online: boolean
  players: number | null
  max_players: number | null
}

/** Asks again this often while the tab is visible; the panel keeps an answer 15 s. */
const EVERY = 30_000

export interface Loaded<T> {
  /** undefined while asking the first time. */
  data: T | undefined
  /** The last ask failed; data, if any, is the one before. */
  failed: boolean
  reload: () => void
}

async function read<T>(path: string): Promise<T> {
  const response = await fetch(path, { cache: 'no-store', headers: { Accept: 'application/json' } })
  if (!response.ok) throw Object.assign(new Error(response.statusText), { status: response.status })
  return (await response.json()) as T
}

/** Reads `path`, then again every thirty seconds while the page is looked at. */
export function useLive<T>(path: string | null, live = true): Loaded<T> & { missing: boolean } {
  const [data, setData] = useState<T | undefined>(undefined)
  const [failed, setFailed] = useState(false)
  const [missing, setMissing] = useState(false)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((value) => value + 1), [])

  useEffect(() => {
    if (!path) return
    let current = true
    const load = () =>
      read<T>(path)
        .then((value) => {
          if (!current) return
          setData(value)
          setFailed(false)
          setMissing(false)
        })
        .catch((error: { status?: number }) => {
          if (!current) return
          setFailed(true)
          setMissing(error.status === 404)
        })
    void load()
    if (!live) return () => void (current = false)
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load()
    }, EVERY)
    const onShow = () => document.visibilityState === 'visible' && void load()
    document.addEventListener('visibilitychange', onShow)
    return () => {
      current = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onShow)
    }
  }, [path, live, tick])

  return { data, failed, missing, reload }
}

export const usePage = () => useLive<Page>('/api/public/status')
export const useCity = () => useLive<City>('/api/public/site/status')
export const useEvent = (id: number) => useLive<StatusEvent>(`/api/public/status/events/${id}`)
export const useHistory = (page: number) =>
  useLive<History>(`/api/public/status/history?page=${page}`, false)

export const FEED = '/api/public/status/feed.atom'
export const DISCORD = 'https://discord.gg/unity-roleplay-608661985747271691'
export const SITE = 'https://unityrp.it/'
